import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronRight, ChevronLeft, X, Play, Pause, LayoutDashboard,
         ClipboardList, Sparkles, Award, Globe, DollarSign } from "lucide-react";

const STEPS = [
  {
    id: "welcome",
    title: "Welcome to DeCarbonIQ",
    subtitle: "Climate & Circularity Intelligence Platform",
    body: "DeCarbonIQ is an institutional-grade platform for GHG measurement, credentialing, and climate intelligence — built for Africa and emerging markets. This demo walks through the key capabilities.",
    navigate: "/dashboard",
    icon: LayoutDashboard,
    highlight: "dashboard",
    tip: "Show the homepage first, then log in with your admin credentials",
  },
  {
    id: "dashboard",
    title: "Climate Intelligence Dashboard",
    subtitle: "Real-time emissions at a glance",
    body: "The dashboard surfaces total GHG inventory (97.59 tCO2e), scope breakdown, data confidence (94.2% HIGH), and recent assessments — instantly readable by senior management.",
    navigate: "/dashboard",
    icon: LayoutDashboard,
    highlight: "kpi-cards",
    tip: "Point to the KPI cards and the Scope Breakdown panel with calculation hash.",
  },
  {
    id: "assessment",
    title: "Assessment Workspace",
    subtitle: "Structured 7-tab GHG inventory workflow",
    body: "Each assessment moves through a governed workflow: data collection → analysis → review → approved → issued. Completeness rules block approval if required data is missing.",
    navigate: "/assessments/1",
    icon: ClipboardList,
    highlight: "assessment-status",
    tip: "Show the 7 tabs: Overview, Methodology, Activity Data, Evidence, Calculations, Results, Reports.",
  },
  {
    id: "factors",
    title: "97-Factor Africa Library",
    subtitle: "Africa-specific, methodology-governed",
    body: "97 approved emission factors including country-specific electricity grids for 22 African nations. South Africa: 0.95 kgCO2e/kWh. Ethiopia: 0.08 kgCO2e/kWh. All sourced from IEA, IPCC, DEFRA.",
    navigate: "/methodologies",
    icon: ClipboardList,
    highlight: "factors-table",
    tip: "Show the Factor Library tab. Filter by 'electricity' to show the African grid factors.",
  },
  {
    id: "insights",
    title: "AI Executive Intelligence",
    subtitle: "Risks, opportunities, and recommended actions",
    body: "Claude Sonnet generates structured executive analysis: key findings, material risks, strategic opportunities, and 3 prioritized recommended actions with estimated impact. Cached after first generation.",
    navigate: "/assessments/1",
    icon: Sparkles,
    highlight: "executive-insights",
    tip: "Click 'Generate Executive Intelligence Report' in the Results tab. Show the structured output.",
  },
  {
    id: "passport",
    title: "Climate Passport",
    subtitle: "Shareable institutional credential",
    body: "A premium credential page showing passport ID, 5-pillar scorecard, methodology governance, calculation hash, and expiry countdown. Download as boardroom-quality PDF with embedded charts.",
    navigate: "/passport/1",
    icon: Award,
    highlight: "passport-document",
    tip: "Download the PDF to show boardroom-quality output. Show the score ring and confidence badge.",
  },
  {
    id: "registry",
    title: "Public Registry",
    subtitle: "Verified, immutable, publicly searchable",
    body: "Published credentials are immutable snapshots. Each record includes verification level, methodology version, calculation hash, and expiry status. The embeddable badge drives organic discovery.",
    navigate: "/registry",
    icon: Globe,
    highlight: "registry-table",
    tip: "Show the registry table. Explain that each record is locked at publication time.",
  },
  {
    id: "pricing",
    title: "Commercial Plans",
    subtitle: "From free to enterprise",
    body: "Transparent 5-tier pricing: Starter (free), SME Verified ($49/mo), Corporate ($199/mo), Financial Institution ($499/mo), and Enterprise (Custom). Annual billing saves 20%. Enterprise custom pricing available for governments, DFIs, and large corporates.",
    navigate: "/pricing",
    icon: DollarSign,
    highlight: "pricing-plans",
    tip: "Show the annual billing toggle. Point to the feature comparison table.",
  },
];

const STORAGE_KEY = "decarboniq_demo_tour";

export function useDemoTour() {
  const [active, setActive] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("demo") === "true") {
      setActive(true);
    }
  }, []);

  const start = () => setActive(true);
  const stop  = () => setActive(false);

  return { active, start, stop };
}

export default function DemoTour({ onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [minimized, setMinimized] = useState(false);

  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  const goTo = useCallback((idx) => {
    setStep(idx);
    if (STEPS[idx].navigate) {
      navigate(STEPS[idx].navigate);
    }
  }, [navigate]);

  const next = () => !isLast ? goTo(step + 1) : onClose?.();
  const prev = () => step > 0 && goTo(step - 1);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight" || e.key === "Enter") setStep(s => Math.min(s + 1, STEPS.length - 1));
      if (e.key === "ArrowLeft") setStep(s => Math.max(s - 1, 0));
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  if (minimized) {
    return (
      <div
        className="fixed bottom-6 right-6 z-50 bg-[#0F172A] text-white rounded-full px-4 py-2.5 flex items-center gap-2 cursor-pointer shadow-xl hover:bg-[#1E293B] transition-colors"
        onClick={() => setMinimized(false)}
        data-testid="demo-tour-minimized"
      >
        <Play size={13} className="text-[#059669]" />
        <span className="text-xs font-semibold">Demo Mode</span>
        <span className="text-xs text-slate-400">{step + 1}/{STEPS.length}</span>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-80 bg-[#0F172A] rounded-xl shadow-2xl border border-slate-700 overflow-hidden"
         data-testid="demo-tour-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-[#059669] rounded-full animate-pulse" />
          <span className="text-xs font-bold text-white uppercase tracking-widest">Demo Mode</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setMinimized(true)}
            className="p-1 text-slate-400 hover:text-white transition-colors rounded">
            <Pause size={13} />
          </button>
          <button onClick={() => onClose?.()} data-testid="close-demo-tour"
            className="p-1 text-slate-400 hover:text-red-400 transition-colors rounded">
            <X size={13} />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-slate-800">
        <div className="h-full bg-[#059669] transition-all duration-300"
             style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
      </div>

      {/* Step content */}
      <div className="p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-8 h-8 bg-emerald-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon size={15} className="text-[#059669]" />
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-0.5">{step + 1} of {STEPS.length}</p>
            <h3 className="text-sm font-bold text-white">{current.title}</h3>
            <p className="text-xs text-[#059669] font-medium">{current.subtitle}</p>
          </div>
        </div>

        <p className="text-xs text-slate-300 leading-relaxed mb-4">{current.body}</p>

        {/* Presenter tip */}
        <div className="bg-amber-900/30 border border-amber-700/40 rounded-lg p-3 mb-4">
          <p className="text-xs font-semibold text-amber-300 mb-1">Presenter tip</p>
          <p className="text-xs text-amber-200/70 leading-relaxed">{current.tip}</p>
        </div>
      </div>

      {/* Step dots */}
      <div className="px-5 pb-2 flex items-center justify-center gap-1.5">
        {STEPS.map((_, i) => (
          <button key={i} onClick={() => goTo(i)}
            className={`w-1.5 h-1.5 rounded-full transition-all ${i === step ? "bg-[#059669] w-4" : "bg-slate-600 hover:bg-slate-400"}`} />
        ))}
      </div>

      {/* Navigation */}
      <div className="px-4 py-3 border-t border-slate-700 flex items-center justify-between gap-2">
        <button onClick={prev} disabled={step === 0}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
          <ChevronLeft size={13} /> Back
        </button>
        <span className="text-xs text-slate-500">← → arrow keys</span>
        <button onClick={next}
          className="flex items-center gap-1 px-4 py-1.5 bg-[#059669] text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 transition-colors">
          {isLast ? "Finish" : "Next"} {!isLast && <ChevronRight size={13} />}
        </button>
      </div>
    </div>
  );
}
